import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useAuth } from '@/lib/auth';
import { prospectsAPI, wallAPI } from '@/lib/api';
import { loadProspectDetail, prospectDetailKey } from '@/lib/resources';
import { getCached, invalidate, invalidatePrefix } from '@/lib/dataCache';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
    Phone, Mail, MapPin, Edit, Trash2, 
    Zap, HardHat, Users, Crown, Building2, XCircle, AlertTriangle,
    TrendingUp, Wallet
} from 'lucide-react';
import { ClickableUser } from '@/components/shared/ClickableUser';
import { OverviewButton, canGoBack } from '@/components/common/NavButtons';import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { shouldShowLeaveHint } from '@/lib/prospectLeaveHint';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { StageChanger } from '@/components/prospect/StageChanger';
import { ProspectAttachments } from '@/components/prospect/ProspectAttachments';
import { EasyPowerModule } from '@/components/prospect/EasyPowerModule';
import { CommissionUnifiee } from '@/components/prospect/CommissionUnifiee';
import { ProspectComponents } from '@/components/prospect/ProspectComponents';
import { Prolongements } from '@/components/prospect/Prolongements';
import ChantiersTabs from '@/components/prospect/ChantiersTabs';
import ChantierAuditorSection from '@/components/prospect/ChantierAuditorSection';
import { ChantierSection } from '@/components/prospect/ChantierSection';
import { QuickTaskEventCreator } from '@/components/prospect/QuickTaskEventCreator';
import ProspectWall from '@/components/prospect/ProspectWall';
import StudyFormsSection from '@/components/study/StudyFormsSection';
import { StageProgressBar } from '@/components/prospect/StageProgressBar';
import ChantierSubSteps from '@/components/prospect/ChantierSubSteps';
import { ReassignApporteur } from '@/components/prospect/ReassignApporteur';
import { RequestDeletionButton } from '@/components/prospect/RequestDeletionButton';
import { CoproprietesSection } from '@/components/prospect/CoproprietesSection';

const CLASS_LABELS = {
    agri: 'Agriculture',
    tertiaire: 'Tertiaire',
    renovation: 'Rénovation globale'
};

export default function ProspectDetail() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    const [prospect, setProspect] = useState(null);
    const [loading, setLoading] = useState(true);
    // Onglet actif (contrôlé) — ouvre le bon module si ?wall=easy_power|chantier
    const initialWall = searchParams.get('wall');
    const [activeTab, setActiveTab] = useState(
        initialWall === 'chantier' || initialWall === 'easy_power' ? initialWall : 'easy_power'
    );
    // Pastilles "non lus" par module pour ce dossier
    const [tabUnread, setTabUnread] = useState({ easy_power: 0, chantier: 0 });
    
    // Attachments
    const [attachments, setAttachments] = useState([]);
    // Erreur transitoire (timeout/réseau/5xx) → on N'affiche PAS "introuvable", on propose de réessayer
    const [loadError, setLoadError] = useState(false);
    // Rappel "sécuriser le dossier avec une tâche" à la sortie de la fiche
    const [leaveHintOpen, setLeaveHintOpen] = useState(false);

    const doLeave = () => {
        setLeaveHintOpen(false);
        if (canGoBack()) navigate(-1); else navigate('/prospects');
    };
    const handleBack = () => {
        if (shouldShowLeaveHint(user)) setLeaveHintOpen(true);
        else doLeave();
    };

    const isAdminFull = (user?.role === 'admin_full' || user?.is_admin === true);
    const isAdmin = (user?.role === 'admin' || user?.is_admin === true);
    const isVP = user?.role === 'vp';
    const isApporteur = user?.role === 'apporteur';
    
    // Tous les utilisateurs peuvent supprimer (avec vérifications côté backend)
    const canDelete = true;

    useEffect(() => {
        loadProspect();
    }, [id]);

    // Pastilles "nouveaux messages" par module pour ce dossier (1 requête légère)
    useEffect(() => {
        wallAPI.unreadBatch([id]).then((res) => {
            const m = res.data?.[id] || {};
            setTabUnread({ easy_power: m.easy_power || 0, chantier: m.chantier || 0 });
        }).catch(() => {});
    }, [id]);

    const clearTabUnread = (module) => setTabUnread((prev) => ({ ...prev, [module]: 0 }));

    const loadProspect = async () => {
        const key = prospectDetailKey(id);
        const cached = getCached(key);
        if (cached) {
            setProspect(cached.prospect);
            setAttachments(cached.attachments);
            setLoading(false);
        } else {
            setLoading(true);
        }
        setLoadError(false);
        try {
            const data = await loadProspectDetail(id);
            setProspect(data.prospect);
            setAttachments(data.attachments);
        } catch (error) {
            const status = error.response?.status;
            // 404 (vraiment introuvable) ou 403 (pas d'accès) → on redirige.
            // Toute autre erreur (timeout, réseau, 502/503/504) = transitoire sous charge :
            // on NE redirige PAS et on NE dit PAS "introuvable" → on propose de réessayer.
            if (status === 404 || status === 403) {
                if (!cached) {
                    toast.error(status === 403 ? 'Accès non autorisé à ce dossier' : 'Dossier introuvable');
                    navigate('/prospects');
                }
            } else if (!cached) {
                console.error('Error loading prospect (transient):', error);
                setLoadError(true);
            }
        } finally {
            setLoading(false);
        }
    };

    // Après une mutation : invalider les caches concernés puis recharger en forçant.
    const refreshProspect = async () => {
        invalidate(prospectDetailKey(id));
        invalidatePrefix('prospects');
        invalidatePrefix('dashboard');
        invalidatePrefix('pilotage');
        invalidatePrefix('userprofile');
        try {
            const data = await loadProspectDetail(id, { force: true });
            setProspect(data.prospect);
            setAttachments(data.attachments);
        } catch { /* ignore */ }
    };

    const handleDelete = async () => {
        if (!window.confirm('Mettre ce dossier à la corbeille ? Il pourra être restauré par un administrateur (Réglages → Maintenance → Corbeille).')) return;
        
        try {
            await prospectsAPI.delete(id);
            toast.success('Dossier mis à la corbeille');
            navigate('/prospects');
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors de la suppression';
            toast.error(message);
        }
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined) return '-';
        return `${value.toLocaleString('fr-FR')} €`;
    };


    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-64 w-full" />
                </div>
            </DashboardLayout>
        );
    }

    if (loadError && !prospect) {
        return (
            <DashboardLayout>
                <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4" data-testid="prospect-load-error">
                    <div className="text-center">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center">
                            <AlertTriangle className="w-8 h-8 text-orange-600" />
                        </div>
                        <h2 className="text-xl font-semibold text-slate-900 mb-2">Chargement impossible</h2>
                        <p className="text-slate-600 mb-4 max-w-md">
                            Le serveur met plus de temps que prévu à répondre (affluence). Le dossier n'est pas perdu — réessayez dans un instant.
                        </p>
                        <div className="flex items-center justify-center gap-3">
                            <Button variant="outline" onClick={() => navigate('/prospects')} data-testid="prospect-error-back">
                                Retour aux dossiers
                            </Button>
                            <Button
                                onClick={() => { setLoading(true); loadProspect(); }}
                                className="bg-purple-600 hover:bg-purple-700"
                                data-testid="prospect-error-retry"
                            >
                                Réessayer
                            </Button>
                        </div>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    if (!prospect) return null;

    return (
        <DashboardLayout>
            <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
                {/* Header */}
                <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={handleBack} data-testid="back-btn" className="text-slate-600 hover:text-slate-900 shrink-0">
                            <ArrowLeft className="w-4 h-4 mr-1" />
                            <span className="hidden sm:inline">Retour</span>
                        </Button>
                        <OverviewButton testid="prospect-overview-btn" />
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                            <span className="text-base sm:text-xl font-semibold text-slate-600">
                                {prospect.first_name?.[0]}{prospect.last_name?.[0]}
                            </span>
                        </div>
                        <div className="min-w-0 flex-1">
                            <h1 className="text-lg sm:text-2xl font-outfit font-semibold text-slate-900 truncate">
                                {prospect.company_name || `${prospect.first_name} ${prospect.last_name}`}
                            </h1>
                            <p className="text-slate-500 text-xs sm:text-base truncate">
                                {CLASS_LABELS[prospect.prospect_class] || 'Non classé'}
                                {prospect.prospect_subclass && ` - ${prospect.prospect_subclass}`}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <QuickTaskEventCreator prospect={prospect} onCreated={refreshProspect} />
                        <Button variant="outline" size="sm" onClick={() => navigate(`/prospects/${id}/edit`)} data-testid="edit-prospect-btn">
                            <Edit className="w-4 h-4 mr-1" />
                            <span className="hidden sm:inline">Modifier</span>
                        </Button>
                        <Button variant="outline" size="sm" onClick={handleDelete} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="delete-prospect-btn">
                            <Trash2 className="w-4 h-4 mr-1" />
                            <span className="hidden sm:inline">Supprimer</span>
                        </Button>
                    </div>
                </div>

                {/* Propriété du dossier - Apporteur | VP | Équipe */}
                {(prospect.dossier_apporteur_name || prospect.dossier_vp_name || prospect.team_name) && (
                    <div className="flex flex-wrap items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid="prospect-ownership">
                        {/* Apporteur - toujours visible */}
                        {prospect.dossier_apporteur_name && (
                            <div className="flex items-center gap-1.5 text-sm">
                                <Users className="w-4 h-4 text-green-600" />
                                <span className="text-slate-500">Apporteur:</span>
                                <span className="font-medium text-green-700">
                                    <ClickableUser userId={prospect.dossier_apporteur_id} name={prospect.dossier_apporteur_name} />
                                </span>
                            </div>
                        )}
                        {/* Séparateur */}
                        {prospect.dossier_apporteur_name && (prospect.dossier_vp_name || prospect.vp_display_name) && (
                            <span className="text-slate-300">|</span>
                        )}
                        {/* VP - selon rôle */}
                        {(prospect.dossier_vp_name || prospect.vp_display_name) && (
                            <div className="flex items-center gap-1.5 text-sm">
                                <Crown className="w-4 h-4 text-purple-600" />
                                <span className="text-slate-500">VP:</span>
                                <span className="font-medium text-purple-700">
                                    <ClickableUser userId={prospect.dossier_vp_id} name={prospect.vp_display_name || prospect.dossier_vp_name} />
                                </span>
                            </div>
                        )}
                        {/* Séparateur */}
                        {(prospect.dossier_apporteur_name || prospect.dossier_vp_name) && (prospect.team_name || prospect.is_vp_direct) && (
                            <span className="text-slate-300">|</span>
                        )}
                        {/* Équipe */}
                        {prospect.team_name && (
                            <div className="flex items-center gap-1.5 text-sm">
                                <Building2 className="w-4 h-4 text-blue-600" />
                                <span className="text-slate-500">Équipe:</span>
                                <span className="font-medium text-blue-700">{prospect.team_name}</span>
                            </div>
                        )}
                        {/* Badge VP Direct */}
                        {prospect.is_vp_direct && !prospect.team_name && (
                            <div className="flex items-center gap-1.5 text-sm">
                                <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-md font-medium text-xs">
                                    Dossier VP Direct
                                </span>
                            </div>
                        )}
                        
                        {/* Bouton Réattribuer pour VP */}
                        {isVP && (prospect.dossier_vp_id === user?.id || prospect.dossier_vp_2_id === user?.id) && (
                            <>
                                <span className="text-slate-300">|</span>
                                <ReassignApporteur prospect={prospect} onReassigned={refreshProspect} />
                            </>
                        )}
                    </div>
                )}

                {/* BANDEAU RAISON DE PERTE - Easy Power */}
                {prospect.easy_power_stage === 'ep_perdu' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="loss-reason-banner-ep">
                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 mt-0.5">
                                <XCircle className="w-5 h-5 text-red-500" />
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                    <Zap className="w-4 h-4 text-amber-600" />
                                    <span className="font-semibold text-red-700">Easy Power - Affaire perdue</span>
                                </div>
                                {prospect.ep_loss_reason && (
                                    <p className="text-sm text-red-600 font-medium">
                                        Raison : {prospect.ep_loss_reason}
                                    </p>
                                )}
                                {prospect.ep_loss_reason_comment && (
                                    <p className="text-sm text-red-600/80 mt-1 italic">
                                        "{prospect.ep_loss_reason_comment}"
                                    </p>
                                )}
                                {prospect.ep_loss_reason_date && (
                                    <p className="text-xs text-red-400 mt-2">
                                        Perdu le {format(new Date(prospect.ep_loss_reason_date), 'dd MMMM yyyy', { locale: fr })}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* BANDEAU RAISON DE PERTE - Chantier */}
                {prospect.chantier_stage === 'ch_perdu' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="loss-reason-banner-ch">
                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 mt-0.5">
                                <XCircle className="w-5 h-5 text-red-500" />
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                    <HardHat className="w-4 h-4 text-blue-600" />
                                    <span className="font-semibold text-red-700">Chantier - Affaire perdue</span>
                                </div>
                                {prospect.ch_loss_reason && (
                                    <p className="text-sm text-red-600 font-medium">
                                        Raison : {prospect.ch_loss_reason}
                                    </p>
                                )}
                                {prospect.ch_loss_reason_comment && (
                                    <p className="text-sm text-red-600/80 mt-1 italic">
                                        "{prospect.ch_loss_reason_comment}"
                                    </p>
                                )}
                                {prospect.ch_loss_reason_date && (
                                    <p className="text-xs text-red-400 mt-2">
                                        Perdu le {format(new Date(prospect.ch_loss_reason_date), 'dd MMMM yyyy', { locale: fr })}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* BADGE SYNTHÈSE CHANTIER - Visible admins uniquement */}
                {(isAdminFull || isAdmin) && prospect.chantier_stage && !prospect.chantier_disabled && (prospect.commission_totale_ch || 0) > 0 && (
                    <div className="relative overflow-hidden rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 via-sky-50 to-indigo-50 p-4 shadow-sm" data-testid="chantier-synthesis-badge">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.08),transparent_60%)] pointer-events-none" />
                        <div className="relative flex items-center gap-2 mb-3">
                            <HardHat className="w-4 h-4 text-blue-700" />
                            <h3 className="text-sm font-semibold text-blue-900 uppercase tracking-wide">
                                Synthèse Chantiers
                            </h3>
                            {prospect.chantiers_count > 0 && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
                                    {prospect.chantiers_count} chantier{prospect.chantiers_count > 1 ? 's' : ''}
                                </span>
                            )}
                        </div>
                        <div className="relative grid grid-cols-2 md:grid-cols-5 gap-3">
                            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-3 border border-blue-100" data-testid="synth-total">
                                <div className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Total HT</div>
                                <div className="text-lg font-bold text-slate-900 mt-0.5">
                                    {(prospect.commission_totale_ch || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €
                                </div>
                            </div>
                            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-3 border border-purple-100" data-testid="synth-vp">
                                <div className="text-[10px] text-slate-500 uppercase tracking-wider font-medium flex items-center gap-1">
                                    <Crown className="w-3 h-3 text-purple-600" /> VP nette
                                </div>
                                <div className="text-lg font-bold text-purple-700 mt-0.5">
                                    {(prospect.ch_commission_vp_payeur_nette || prospect.ch_commission_vp || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €
                                </div>
                            </div>
                            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-3 border border-emerald-100" data-testid="synth-app">
                                <div className="text-[10px] text-slate-500 uppercase tracking-wider font-medium flex items-center gap-1">
                                    <Users className="w-3 h-3 text-emerald-600" /> Apporteur
                                </div>
                                <div className="text-lg font-bold text-emerald-700 mt-0.5">
                                    {(prospect.ch_commission_apporteur_1 || prospect.ch_commission_apporteur || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €
                                </div>
                            </div>
                            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-3 border border-amber-100" data-testid="synth-valnexo">
                                <div className="text-[10px] text-slate-500 uppercase tracking-wider font-medium flex items-center gap-1">
                                    <Building2 className="w-3 h-3 text-amber-600" /> Valnexo
                                </div>
                                <div className="text-lg font-bold text-amber-700 mt-0.5">
                                    {(prospect.ch_restant_valnexo || prospect.ch_commission_valnexo || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €
                                </div>
                            </div>
                            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-3 border border-slate-200" data-testid="synth-at">
                                <div className="text-[10px] text-slate-500 uppercase tracking-wider font-medium flex items-center gap-1">
                                    <Wallet className="w-3 h-3 text-slate-600" /> AT Service
                                </div>
                                <div className="text-lg font-bold text-slate-700 mt-0.5">
                                    {(prospect.ch_restant_at_service || prospect.ch_commission_at_service || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Contact Info */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Informations de Contact</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {prospect.phone && (
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                                        <Phone className="w-5 h-5 text-slate-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-500">Téléphone</p>
                                        <a href={`tel:${prospect.phone}`} className="font-medium text-slate-900 hover:text-blue-600">
                                            {prospect.phone}
                                        </a>
                                    </div>
                                </div>
                            )}
                            {prospect.email && (
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                                        <Mail className="w-5 h-5 text-slate-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-500">Email</p>
                                        <a href={`mailto:${prospect.email}`} className="font-medium text-slate-900 hover:text-blue-600">
                                            {prospect.email}
                                        </a>
                                    </div>
                                </div>
                            )}
                            {(prospect.address || prospect.city) && (
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                                        <MapPin className="w-5 h-5 text-slate-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-500">Adresse</p>
                                        <p className="font-medium text-slate-900">
                                            {prospect.address && `${prospect.address}, `}
                                            {prospect.postal_code} {prospect.city}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Project Info */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Détails du Dossier</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <p className="text-sm text-slate-500">Classification</p>
                                <p className="font-medium text-slate-900">
                                    {CLASS_LABELS[prospect.prospect_class] || 'Non classé'}
                                    {prospect.prospect_subclass && ` → ${prospect.prospect_subclass}`}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-slate-500">Créé le</p>
                                <p className="font-medium text-slate-900">
                                    {format(new Date(prospect.created_at), 'dd MMMM yyyy', { locale: fr })}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-slate-500">Dernière mise à jour</p>
                                <p className="font-medium text-slate-900">
                                    {format(new Date(prospect.updated_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* SECTION COPROPRIÉTÉS (uniquement pour un prospect Syndic parent) */}
                {prospect.prospect_class === 'renovation' && prospect.prospect_subclass === 'syndic' && !prospect.parent_prospect_id && (
                    <div className="mb-6">
                        <CoproprietesSection prospectId={prospect.id} enseigne={prospect.enseigne || prospect.company_name} />
                    </div>
                )}

                {/* COMPOSANTES DU DOSSIER (Easy Power / Chantier) */}
                <ProspectComponents prospect={prospect} onUpdate={refreshProspect} />

                {/* ONGLETS EASY POWER / CHANTIER */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-2 mb-6">
                        <TabsTrigger value="easy_power" className="gap-2" disabled={!prospect.easy_power_stage || prospect.easy_power_disabled}>
                            <Zap className="w-4 h-4" />
                            Easy Power
                            {tabUnread.easy_power > 0 && (
                                <span data-testid="tab-wall-badge-easy_power" className="min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-purple-600 text-white text-[10px] font-bold leading-none">
                                    {tabUnread.easy_power > 9 ? '9+' : tabUnread.easy_power}
                                </span>
                            )}
                            {prospect.easy_power_disabled && <span className="text-xs opacity-50">(masqué)</span>}
                        </TabsTrigger>
                        <TabsTrigger value="chantier" className="gap-2" disabled={!prospect.chantier_stage || prospect.chantier_disabled}>
                            <HardHat className="w-4 h-4" />
                            Chantier
                            {tabUnread.chantier > 0 && (
                                <span data-testid="tab-wall-badge-chantier" className="min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-purple-600 text-white text-[10px] font-bold leading-none">
                                    {tabUnread.chantier > 9 ? '9+' : tabUnread.chantier}
                                </span>
                            )}
                            {prospect.chantier_disabled && <span className="text-xs opacity-50">(masqué)</span>}
                        </TabsTrigger>
                    </TabsList>
                    
                    {/* ==================== ONGLET EASY POWER ==================== */}
                    <TabsContent value="easy_power" className="space-y-6">
                        {/* Barre de progression des étapes */}
                        <StageProgressBar prospect={prospect} pipelineType="easy_power" onUpdate={refreshProspect} />

                        {/* MUR DU DOSSIER — Easy Power (entre pipeline et commissions) */}
                        <ProspectWall prospectId={prospect.id} module="easy_power" moduleLabel="Easy Power" onRead={clearTabUnread} />

                        {/* COMMISSION UNIFIÉE EP */}
                        <CommissionUnifiee prospect={prospect} onUpdate={refreshProspect} pipelineType="easy_power" />

                        {/* MODULE EASY POWER STRUCTURÉ */}
                        <EasyPowerModule prospect={prospect} onUpdate={refreshProspect} />

                        {/* Stage Changer EP - Progression du dossier */}
                        <StageChanger prospect={prospect} onStageChanged={refreshProspect} pipelineType="easy_power" />

                        {/* Prolongements Easy Power */}
                        <Prolongements 
                            prospectId={id}
                            prospect={prospect}
                            onRefresh={refreshProspect}
                        />

                        {/* Pièces jointes EP (autres documents) */}
                        <ProspectAttachments 
                            prospectId={id}
                            attachments={(attachments || []).filter(a => a.category !== 'chantier' && a.attachment_type !== 'FACTURE')}
                            onRefresh={refreshProspect}
                            canDelete={isAdminFull || isAdmin || isVP}
                            category="easy_power"
                            title="Autres Pièces Jointes"
                        />
                    </TabsContent>
                    
                    {/* ==================== ONGLET CHANTIER ==================== */}
                    <TabsContent value="chantier" className="space-y-6">
                        {/* Barre de progression des étapes Chantier */}
                        <StageProgressBar prospect={prospect} pipelineType="chantier" onUpdate={refreshProspect} readOnly={prospect?.is_auditor_view === true} />

                        {/* Auditeur du dossier (chantier uniquement) */}
                        <ChantierAuditorSection prospect={prospect} onUpdate={refreshProspect} />

                        {/* RELEVÉ D'ÉTUDE SUR PLACE (natif, hors-ligne) — sous le pipeline, au-dessus du Mur */}
                        <StudyFormsSection prospectId={prospect.id} />

                        {/* MUR DU DOSSIER — Chantier (entre pipeline et commissions) */}
                        <ProspectWall prospectId={prospect.id} module="chantier" moduleLabel="Chantier" onRead={clearTabUnread} />

                        {/* Sous-étapes structurées (Lot 4) selon l'étape Chantier */}
                        <ChantierSubSteps
                            key={prospect.chantier_stage}
                            stage={prospect.chantier_stage}
                            substeps={prospect.chantier_substeps}
                            readOnly={prospect?.is_auditor_view === true}
                            onSave={async (substeps) => {
                                try {
                                    await prospectsAPI.updateChantierSubsteps(prospect.id, substeps);
                                    refreshProspect();
                                } catch (e) {
                                    toast.error("Erreur lors de l'enregistrement des sous-étapes");
                                    throw e;
                                }
                            }}
                        />

                        {/* MULTI-CHANTIERS INDÉPENDANTS */}
                        <ChantiersTabs prospectId={prospect.id} onUpdate={refreshProspect} isAuditorView={prospect?.is_auditor_view === true} />

                        {/* Stage Changer Chantier (global — masqué pour l'auditeur) */}
                        {!(prospect?.is_auditor_view === true) && (
                            <StageChanger prospect={prospect} onStageChanged={refreshProspect} pipelineType="chantier" />
                        )}
                    </TabsContent>
                </Tabs>
            </div>

            {/* Rappel de sortie : sécuriser le dossier avec une tâche */}
            <Dialog open={leaveHintOpen} onOpenChange={setLeaveHintOpen}>
                <DialogContent className="max-w-md" data-testid="prospect-leave-hint">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ShieldCheck className="w-5 h-5 text-emerald-600" />
                            Avez-vous sécurisé ce dossier ?
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-slate-600">
                        Avant de quitter <strong>{prospect.company_name || `${prospect.first_name} ${prospect.last_name}`}</strong>,
                        pensez à le <strong>sécuriser avec une tâche</strong> (relance, rappel, prochaine action…) afin de ne rien oublier
                        et de garder le dossier vivant. Un dossier suivi, c'est un dossier qui se transforme. 💪
                    </p>
                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button variant="outline" onClick={doLeave} data-testid="leave-hint-quit">
                            Quitter quand même
                        </Button>
                        <Button onClick={() => setLeaveHintOpen(false)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="leave-hint-create-task">
                            Créer une tâche maintenant
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}

// Composant pour les notes éditables — retiré (remplacé par le Mur du dossier)

